'use client';

/**
 * The frame every account surface shares — sign-in, forgot password, reset
 * password, sign-up, email verification.
 *
 * Each of those centred its own column, repeated its own wordmark markup, and
 * wrapped its form in `.form-container`, whose `min-width: 400px` on inputs put
 * a horizontal scrollbar on every phone narrower than ~470px. One frame: the
 * brand mark over the display-serif page title, the form on a warm panel, and
 * a quiet footer line for the way out.
 */

import type { ReactNode } from 'react';
import Link from 'next/link';
import { BrandMark } from '@/components/ui/BrandMark';
import { useTranslation } from '@/hooks/useTranslation';

export interface AuthShellProps {
  /** The page's h1, set in the display serif. */
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Quiet line below the panel — "back to sign in", "create an account". */
  footer?: ReactNode;
  /** `md` for the single-column forms, `lg` for the sign-up wizard. */
  width?: 'md' | 'lg';
}

const WIDTH_CLASSES = {
  md: 'max-w-md',
  lg: 'max-w-xl',
} as const;

export function AuthShell({
  title,
  description,
  children,
  footer,
  width = 'md',
}: AuthShellProps) {
  const { t } = useTranslation();

  return (
    <div className="container flex min-h-full items-center justify-center py-12">
      <div className={`w-full ${WIDTH_CLASSES[width]}`}>
        <div className="mb-8 text-center">
          <Link
            href="/"
            className="logo inline-flex items-center gap-2.5 hover:no-underline"
          >
            <BrandMark size={36} />
            <span>{t('logo.headerTitle', 'CV Matcher')}</span>
          </Link>

          <h1 className="mt-6">{title}</h1>

          {description && (
            <div className="mx-auto mt-2 max-w-[52ch] text-secondary">
              {description}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-primary bg-secondary p-6 shadow-sm sm:p-8">
          {children}
        </div>

        {footer && (
          <div className="mt-6 text-center text-sm text-secondary">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthShell;
